import { Grid, Typography } from "@material-ui/core";
import { clsx } from "clsx";
import { useSetAtom } from "jotai";
import { assetsUrl } from "../../../common/config";
import { currentGamePlanIndexAtom } from "../../../components/wizard/atoms";
import { useGamePlanPath } from "../../../components/wizard/use-game-plan";
import { ModuleBffApi } from "../../../models/api";
import { Status } from "../../../models/gameplan";
import { useHistory } from "../../../shared/hooks/useHistory";
import { startJourneyStyles } from "./start-journey.styles";

const activeJourneyModule = `${assetsUrl}/learning/activeJourneyModule.svg`;
const inactiveJourneyModule = `${assetsUrl}/learning/inactiveJourneyModule.svg`;
const completedJourneyModule = `${assetsUrl}/learning/completedJourneyModule.svg`;

type Props = {
  courseName: string;
  imageUrl: string;
  modules: ModuleBffApi[];
  isAllCompleted: boolean;
};

export const StartJourney = ({
  courseName,
  imageUrl,
  modules,
  isAllCompleted,
}: Props) => {
  const classes = startJourneyStyles();
  const history = useHistory();
  const { flattenGamePlans } = useGamePlanPath();
  const setCurrentGamePlanIndex = useSetAtom(currentGamePlanIndexAtom);

  const getImgUrl = (status: Status) => {
    if (status === Status.COMPLETED) {
      return completedJourneyModule;
    }

    if (status === Status.IN_PROGRESS) {
      return activeJourneyModule;
    }

    return inactiveJourneyModule;
  };

  const handleModuleClick = (module: ModuleBffApi) => () => {
    const flattenModule = flattenGamePlans?.find(
      (gameplan) =>
        gameplan.module?.item?.id === module.id &&
        gameplan.module?.item?.order === module.order
    );
    setCurrentGamePlanIndex(flattenModule?.index || -1);
    history.push("/learningPath");
  };

  const Subtitle = isAllCompleted ? "Great job!" : "Start your learning path!";

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12}>
        <Typography
          variant="subtitle1"
          className={classes.title}
          align="center"
        >
          {Subtitle}
        </Typography>
        <Typography variant="h6" className={classes.courseName} align="center">
          {courseName}
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.imageContainer}>
        <img src={imageUrl} alt="start journey" />
      </Grid>
      <Grid item xs={12} className={classes.modulesContainer}>
        <hr className={classes.dashedLine} />
        {modules.map((module) => (
          <span
            key={module.id}
            role="presentation"
            aria-label={module.title}
            className={clsx(
              classes.moduleDetails,
              { [classes.inProgress]: module.status === Status.IN_PROGRESS },
              { [classes.isCompleted]: module.status === Status.COMPLETED }
            )}
            onClick={handleModuleClick(module)}
          >
            <div className={classes.imageModuleContainer}>
              <img alt={module.title} src={getImgUrl(module.status)} />
            </div>
            <Typography
              variant="subtitle1"
              align="center"
              className={clsx(classes.moduleName, {
                [classes.inProgress]: module.status === Status.IN_PROGRESS,
                [classes.isCompleted]: module.status === Status.COMPLETED,
              })}
            >
              {module.title}
            </Typography>
          </span>
        ))}
      </Grid>
    </Grid>
  );
};
